import client from "./client";

export type BandStatus = "below" | "within" | "above" | "no_band";
export type CompChangeType =
  | "hire"
  | "merit_increase"
  | "promotion"
  | "adjustment"
  | "cost_of_living"
  | "bonus"
  | "demotion"
  | "merit"
  | "correction"
  | "initial";
export type CompPeriod = "yearly" | "monthly" | "hourly";

export interface CompensationBand {
  id: string;
  org_id: string;
  title: string;
  department_id: string | null;
  min_salary: number;
  mid_salary: number;
  max_salary: number;
  currency: string;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface BandCreateInput {
  title: string;
  department_id?: string | null;
  min_salary: number;
  mid_salary: number;
  max_salary: number;
  currency?: string;
  notes?: string | null;
  is_active?: boolean;
}

export interface BandUpdateInput {
  title?: string;
  department_id?: string | null;
  min_salary?: number;
  mid_salary?: number;
  max_salary?: number;
  currency?: string;
  notes?: string | null;
  is_active?: boolean;
}

export interface DepartmentCompStat {
  department_id: string | null;
  department_name: string | null;
  employee_count: number;
  avg_salary: number;
  median_salary: number;
  total_payroll: number;
}

export interface CompensationOverview {
  employee_count: number;
  avg_salary: number;
  median_salary: number;
  total_payroll: number;
  currency: string;
  below_band_count: number;
  within_band_count: number;
  above_band_count: number;
  no_band_count: number;
  by_department: DepartmentCompStat[];
}

export interface EmployeeCompRow {
  employee_id: string;
  first_name: string;
  last_name: string;
  title: string;
  department_id: string | null;
  department_name: string | null;
  manager_id: string | null;
  hire_date: string;
  employment_type: string;
  current_amount: number | null;
  currency: string;
  period: CompPeriod;
  last_change_date: string | null;
  band_id: string | null;
  band_min: number | null;
  band_mid: number | null;
  band_max: number | null;
  band_position: number | null;
  band_status: BandStatus;
}

export interface CompensationEntry {
  id: string;
  employee_id: string;
  effective_date: string;
  amount: number;
  currency: string;
  period: CompPeriod;
  change_type: string;
  reason: string | null;
  title_at_time: string | null;
  created_at: string;
}

export interface CompensationChangeInput {
  employee_id: string;
  effective_date: string;
  amount: number;
  currency?: string;
  period?: CompPeriod;
  change_type?: CompChangeType;
  reason?: string | null;
  title_at_time?: string | null;
}

export interface EquityGroupStat {
  key: string;
  label: string;
  employee_count: number;
  avg_salary: number;
  median_salary: number;
  ratio_vs_overall: number;
  flagged: boolean;
}

export interface EquityReport {
  overall_avg: number;
  overall_median: number;
  currency: string;
  by_department: EquityGroupStat[];
  by_tenure: EquityGroupStat[];
  by_employment_type: EquityGroupStat[];
  flag_threshold: number;
}

export async function listBands(includeInactive = false): Promise<CompensationBand[]> {
  const res = await client.get("/compensation/bands", {
    params: { include_inactive: includeInactive },
  });
  return res.data;
}

export async function createBand(input: BandCreateInput): Promise<CompensationBand> {
  const res = await client.post("/compensation/bands", input);
  return res.data;
}

export async function updateBand(
  id: string,
  input: BandUpdateInput,
): Promise<CompensationBand> {
  const res = await client.put(`/compensation/bands/${id}`, input);
  return res.data;
}

export async function fetchOverview(): Promise<CompensationOverview> {
  const res = await client.get("/compensation/overview");
  return res.data;
}

export async function listEmployeeComp(params?: {
  department_id?: string;
  band_status?: BandStatus;
  search?: string;
}): Promise<EmployeeCompRow[]> {
  const res = await client.get("/compensation/employees", { params });
  return res.data;
}

export async function fetchEmployeeHistory(
  employeeId: string,
): Promise<CompensationEntry[]> {
  const res = await client.get(`/compensation/employee/${employeeId}`);
  return res.data;
}

export async function recordChange(
  input: CompensationChangeInput,
): Promise<CompensationEntry> {
  const res = await client.post("/compensation/changes", input);
  return res.data;
}

export async function fetchEquity(flagThreshold = 0.15): Promise<EquityReport> {
  const res = await client.get("/compensation/equity", {
    params: { flag_threshold: flagThreshold },
  });
  return res.data;
}


// ──────────────── Lenavio Comp — market benchmarks ────────────────

export type BenchmarkConfidence = "high" | "medium" | "low";
export type OrgBenchmarkFlag =
  | "underpaid"
  | "within"
  | "overpaid"
  | "no_benchmark"
  | "no_salary";

export interface Benchmark {
  id: string;
  job_title: string;
  department: string | null;
  location: string | null;
  currency: string;
  market_p25: number;
  market_p50: number;
  market_p75: number;
  market_p90: number;
  confidence: BenchmarkConfidence;
  caveats: string | null;
  source: string;
  last_updated: string;
}

export interface BenchmarkRequestInput {
  job_title: string;
  department?: string | null;
  location?: string | null;
  currency?: string;
  refresh?: boolean;
}

export interface OrgBenchmarkRow {
  employee_id: string;
  first_name: string;
  last_name: string;
  title: string;
  department: string | null;
  location: string | null;
  current_salary: number | null;
  currency: string;
  market_p25: number | null;
  market_p50: number | null;
  market_p75: number | null;
  market_p90: number | null;
  benchmark_id: string | null;
  flag: OrgBenchmarkFlag;
}

export interface OrgBenchmarkSummary {
  total_employees: number;
  benchmarked: number;
  underpaid: number;
  overpaid: number;
  no_benchmark: number;
  no_salary: number;
  rows: OrgBenchmarkRow[];
}

export interface PayEquityReport {
  overall_avg: number;
  overall_median: number;
  currency: string;
  by_gender: EquityGroupStat[];
  by_department: EquityGroupStat[];
  by_tenure: EquityGroupStat[];
  flag_threshold: number;
}

export async function lookupBenchmark(
  input: BenchmarkRequestInput,
): Promise<Benchmark> {
  const res = await client.post("/compensation/benchmark", input);
  return res.data;
}

export async function fetchOrgBenchmark(): Promise<OrgBenchmarkSummary> {
  const res = await client.get("/compensation/benchmark/org");
  return res.data;
}

export async function fetchPayEquity(
  flagThreshold = 0.15,
): Promise<PayEquityReport> {
  const res = await client.get("/compensation/equity-report", {
    params: { flag_threshold: flagThreshold },
  });
  return res.data;
}
